import { useState, useEffect, useMemo, useRef } from 'react';
import { useDrop } from 'react-dnd';

import Hint from '../../hint';

import { getFormattedCurrency } from '../../../../utils';

import './styles.css';

import SignPlus from './images/icon-plus.svg';
import DropAreaList from './dropAreaList';

export default function DropArea({
  option,
  value,
  isSecondDropArea = false,
  onChangeItemDrop,
  onCoreItemDrop,
  onDropAreaChange,
  resetKey,
  incorrectDecisions,
  onSecondDropAreaItemDrop,
  isItemsDisabled,
  selectedDesignTypes,
  tutorialRestrictions,
  optionsToBeFilled
}) {
  const [droppedItems, setDroppedItems] = useState([]);

  useEffect(() => {
    setDroppedItems([]);
  }, [resetKey]);

  const currentDropAreaIncorrectDecisions = useMemo(() => 
    incorrectDecisions?.filter(incorrectDecision => incorrectDecision.isSecondDropArea === isSecondDropArea) || [], 
    [incorrectDecisions, isSecondDropArea]
  );
  
  useEffect(() => {
    if(currentDropAreaIncorrectDecisions.length) {
      setDroppedItems(prevItems => {
        return prevItems.map(item => {
          const isIncorrect = currentDropAreaIncorrectDecisions.some(incorrectDecision => incorrectDecision.id === item.id);
          return isIncorrect ? { ...item, isIncorrect: true } : item;
        });
      });
    }
  }, [currentDropAreaIncorrectDecisions.length]);

  const handleItemDrop = (item) => {
    if (tutorialRestrictions) {
      const isAllowedOption = tutorialRestrictions.allowedOptionName === option.name;
      const isAllowedItem = tutorialRestrictions.allowedItemDescription === item.description;
      
      if (!isAllowedOption || !isAllowedItem) {
        return; // Reject the drop
      }
    }
    
    setDroppedItems(prevItems => {
      const boxContainsDebit = prevItems.some(droppedItem => droppedItem.name === 'Debit');
      const boxContainsCredit = prevItems.some(droppedItem => droppedItem.name === 'Credit');
      const boxContainsIncrease = prevItems.some(droppedItem => droppedItem.description === 'Increase');
      const boxContainsDecrease = prevItems.some(droppedItem => droppedItem.description === 'Decrease');

      const isAssetCategory = option.name === 'Assets';
      const isLiabilityOrEquityCategory = option.name === 'Liabilities' || option.name === 'Equity';

      if (
        (isAssetCategory && ((item.description === 'Increase' && boxContainsCredit) || (item.description === 'Decrease' && boxContainsDebit))) ||
        (isLiabilityOrEquityCategory && ((item.description === 'Increase' && boxContainsDebit) || (item.description === 'Decrease' && boxContainsCredit))) ||
        (isAssetCategory && ((item.name === 'Credit' && boxContainsIncrease) || (item.name === 'Debit' && boxContainsDecrease))) ||
        (isLiabilityOrEquityCategory && ((item.name === 'Debit' && boxContainsIncrease) || (item.name === 'Credit' && boxContainsDecrease)))
      ) {
        return prevItems;
      }
      
      const itemTypeExists = prevItems.some(droppedItem => droppedItem.type === item.type);
      let updatedDroppedItems;
      let updatedValue = 0;
  
      if (itemTypeExists) {
        updatedDroppedItems = prevItems.map(prevItem => {
          if (prevItem.type === item.type) {
            if(item.type === 'change') {
              if(onSecondDropAreaItemDrop) {
                onSecondDropAreaItemDrop('change', item.description);
              }
              
              if (prevItem.description !== item.description) {
                updatedValue = calculateUpdatedValue(item.description, value * 2);
                onChangeItemDrop(option.name, updatedValue);

                const droppedCoreItem = prevItems.find(prevItem => prevItem.type === 'core');
                if(droppedCoreItem) {
                  onCoreItemDrop(option.name, {name: droppedCoreItem.name, value: updatedValue}, 'add');
                }
              } else {
                return prevItem;
              }
            } else if (item.type === 'transaction' && !selectedDesignTypes.includes('1') && selectedDesignTypes.includes('2')) {
              if(onSecondDropAreaItemDrop) {
                onSecondDropAreaItemDrop('transaction', item.name);
              }

              if (prevItem.name !== item.name) {
                updatedValue = calculateNormalUpdatedValue(option.name, item.name, value * 2);
                onChangeItemDrop(option.name, updatedValue);

                const droppedCoreItem = prevItems.find(prevItem => prevItem.type === 'core');
                if(droppedCoreItem) {
                  onCoreItemDrop(option.name, {name: droppedCoreItem.name, value: updatedValue}, 'add');
                }
              } else {
                return prevItem;
              }
            } else if(item.type === 'core') {
              if(onSecondDropAreaItemDrop) {
                onSecondDropAreaItemDrop('core', item.name);
              }

              let updatedValue;
              if(selectedDesignTypes.includes('1')) {
                const droppedChangeItem = prevItems.find(prevItem => prevItem.type === 'change');
                updatedValue = droppedChangeItem ? calculateUpdatedValue(droppedChangeItem.description, value) : 0;
                
                if(droppedChangeItem) {
                  const oppositeChange = droppedChangeItem.description === 'Increase' ? 'Decrease' : 'Increase';
                  updatedValue = calculateUpdatedValue(oppositeChange, value);
                  onCoreItemDrop(option.name, {name: prevItem.name, value: updatedValue}, 'add');
                }
  
                updatedValue = droppedChangeItem ? calculateUpdatedValue(droppedChangeItem.description, value) : 0;
              } else if(selectedDesignTypes.includes('2')) {
                const droppedTransactionItem = prevItems.find(prevItem => prevItem.type === 'transaction');

                if(droppedTransactionItem) {
                  const oppositeChange = droppedTransactionItem.name === 'Debit' ? 'Credit' : 'Debit';
                  updatedValue = calculateNormalUpdatedValue(option.name, oppositeChange, value);
                  onCoreItemDrop(option.name, {name: prevItem.name, value: updatedValue}, 'add');
                }

                updatedValue = droppedTransactionItem ? calculateNormalUpdatedValue(option.name, droppedTransactionItem.name, value) : 0;
              }
              
              onCoreItemDrop(option.name, {name: item.name, value: updatedValue}, 'add');
            }
            return item;
          }
          return prevItem;
        });
      } else {
        updatedDroppedItems = [...prevItems, item];
        if (item.type === 'change') {
          if(onSecondDropAreaItemDrop) {
            onSecondDropAreaItemDrop('change', item.description);
          }

          updatedValue = calculateUpdatedValue(item.description, value);
          onChangeItemDrop(option.name, updatedValue);
          const formattedValue = getFormattedCurrency(value);
          const valueItem = { type: 'value', name: formattedValue, description: formattedValue };
          updatedDroppedItems = [...updatedDroppedItems, valueItem];

          const droppedCoreItem = prevItems.find(prevItem => prevItem.type === 'core');
          if(droppedCoreItem) {
            updatedValue = calculateUpdatedValue(item.description, value);
            onCoreItemDrop(option.name, {name: droppedCoreItem.name, value: updatedValue}, 'add');
          }
        } else if (item.type === 'transaction') {
          if(onSecondDropAreaItemDrop) {
            onSecondDropAreaItemDrop('transaction', item.name);
          }
          if(!selectedDesignTypes.includes('1') && selectedDesignTypes.includes('2')) {
            updatedValue = calculateNormalUpdatedValue(option.name, item.name, value);
            onChangeItemDrop(option.name, updatedValue);

            const droppedCoreItem = prevItems.find(prevItem => prevItem.type === 'core');
            if(droppedCoreItem) {
              updatedValue = calculateNormalUpdatedValue(option.name, item.name, value);
              onCoreItemDrop(option.name, {name: droppedCoreItem.name, value: updatedValue}, 'add');
            }
          }
        } else if (item.type === 'core') {
          if(onSecondDropAreaItemDrop) {
            onSecondDropAreaItemDrop('core', item.name);
          }

          let updatedValue;
          if(selectedDesignTypes.includes('1')) {
            const droppedChangeItem = prevItems.find(prevItem => prevItem.type === 'change');
            updatedValue = droppedChangeItem ? calculateUpdatedValue(droppedChangeItem.description, value) : 0;
          } else if(selectedDesignTypes.includes('2')) {
            const droppedTransactionItem = prevItems.find(prevItem => prevItem.type === 'transaction');
            updatedValue = droppedTransactionItem ? calculateNormalUpdatedValue(option.name, droppedTransactionItem.name, value) : 0;
          }

          onCoreItemDrop(option.name, {name: item.name, value: updatedValue}, 'add');
        }
      }
  
      onDropAreaChange({
        id: `${option.id}_${+isSecondDropArea}`,
        optionAccountItemId: option.accountItemId,
        optionName: option.name,
        droppedItemsCount: updatedDroppedItems.length, 
        box: !isSecondDropArea ? 'Top' : 'Bottom',
        changeId: item.type === 'change' ? item.id : null,
        changeAccountItemId: item.type === 'change' ? item.accountItemId : null,
        changeDescription: item.type === 'change' ? item.description.toLowerCase() : null,
        coreId: item.type === 'core' ? item.id : null,
        coreAccountItemId: item.type === 'core' ? item.accountItemId : null,
        coreName: item.type === 'core' ? item.name : null,
        transactionId: item.type === 'transaction' ? item.id : null,
        transactionAccountItemId: item.type === 'transaction' ? item.accountItemId : null,
        transactionName: item.type === 'transaction' ? item.name : null,
      });
      return updatedDroppedItems;
    });
  };
  
  const calculateUpdatedValue = (description, value) => {
    const updatedValue = description === 'Increase' ? value : -value;
    return updatedValue;
  };
  
  const calculateNormalUpdatedValue = (optionName, itemName, value) => {
    const updatedValue = optionName === 'Assets'
      ? itemName === 'Debit' ? value : -value
      : itemName === 'Credit' ? value : -value;
    return updatedValue;
  };
  

  // Deps array required — without it react-dnd captures the spec on
  // first render and never updates. That freezes both `hover` (which
  // closes over droppedItems + prop callbacks) and the `drop`
  // callback (handleItemDrop, which reads current state and calls
  // onDropAreaChange / onChangeItemDrop / onCoreItemDrop). The
  // result of the freeze: dropped chips update local droppedItems
  // but never propagate up, so the Balance Sheet doesn't update and
  // decision results are evaluated against stale data. See the
  // classification/normal drop-area fix for the same pattern.
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'recordOption',
    hover: (item) => {
      if (tutorialRestrictions) {
        const isAllowedOption = tutorialRestrictions.allowedOptionName === option.name;
        const isAllowedItem = tutorialRestrictions.allowedItemDescription === item.description;
        
        if (!isAllowedOption) {
          // Wrong category
          setHintContent(`Incorrect: This is not the ${tutorialRestrictions.allowedOptionName} Element.`);
          return;
        }
        
        if (!isAllowedItem) {
          // Wrong item (e.g., "-" instead of "+")
          const correctSign = tutorialRestrictions.allowedItemDescription === 'Increase' ? '"+"' : '"-"';
          const actionWord = tutorialRestrictions.allowedItemDescription === 'Increase' ? 'increase' : 'decrease';
          setHintContent(`Incorrect. Select the ${correctSign} sign since this transaction will ${actionWord} ${tutorialRestrictions.allowedOptionName}.`);
          return;
        }
      }

      const droppedChangeItemName = droppedItems.find(droppedItem => droppedItem.type === 'change')?.description;
      const droppedChangeItemOppositeName = droppedChangeItemName === 'Increase' ? 'Decrease' : 'Increase';
      const droppedCoreItemName = droppedItems.find(droppedItem => droppedItem.type === 'core')?.name;
      const droppedDebitOrCreditItemName = droppedItems.find(droppedItem => droppedItem.type === 'transaction')?.name;

      const isAssetCategory = option.name === 'Assets';
      const isLiabilityOrEquityCategory = option.name === 'Liabilities' || option.name === 'Equity';
      const optionName = option.name;

      const changeItemName = item.description;
      const changeItemOppositeName = changeItemName === 'Increase' ? 'Decrease' : 'Increase';

      const itemName = item.name;

      const boxContainsDebit = droppedDebitOrCreditItemName === 'Debit';
      const boxContainsCredit = droppedDebitOrCreditItemName === 'Credit';
      const boxContainsIncrease = droppedChangeItemName === 'Increase';
      const boxContainsDecrease = droppedChangeItemName === 'Decrease';

      const changeArticle = changeItemName === "Increase" ? "an" : "a";
      const transactionArticle = droppedChangeItemName === "Increase" ? "an" : "a";
        
      const content = item.type === 'change'
        ? (isAssetCategory && ((changeItemName === 'Increase' && boxContainsCredit) || (changeItemName === 'Decrease' && boxContainsDebit))) || (isLiabilityOrEquityCategory && ((changeItemName === 'Increase' && boxContainsDebit) || (changeItemName === 'Decrease' && boxContainsCredit)))
          ? `Contradictory decision. This box already has a ${droppedDebitOrCreditItemName} to ${optionName} (i.e. ${changeItemOppositeName}) decision, therefore ${changeArticle} ${changeItemName} ${optionName} decision is not allowed.`
          : droppedCoreItemName
            ? `${changeItemName} ${droppedCoreItemName}`
            : `${changeItemName} ${optionName}`
        : item.type === 'transaction'
          ? (isAssetCategory && ((itemName === 'Credit' && boxContainsIncrease) || (itemName === 'Debit' && boxContainsDecrease))) || (isLiabilityOrEquityCategory && ((itemName === 'Debit' && boxContainsIncrease) || (itemName === 'Credit' && boxContainsDecrease)))
              ? `Contradictory decision. This box already has ${transactionArticle} ${droppedChangeItemName} to ${optionName} decision, therefore a ${itemName} ${optionName} (i.e. ${droppedChangeItemOppositeName}) decision is not allowed.`
              : droppedCoreItemName
                ? `${itemName} ${droppedCoreItemName}`
                : `${itemName} ${optionName}`
          : droppedDebitOrCreditItemName
            ? `${droppedDebitOrCreditItemName} ${itemName}`
            : droppedChangeItemName
              ? `${droppedChangeItemName} ${itemName}`
              : null;
      
      setHintContent(content);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver()
    }),
    drop: handleItemDrop
  }), [
    option,
    value,
    isSecondDropArea,
    onChangeItemDrop,
    onCoreItemDrop,
    onDropAreaChange,
    onSecondDropAreaItemDrop,
    selectedDesignTypes,
    tutorialRestrictions,
    droppedItems,
    handleItemDrop,
  ]);

  const [isHintShown, setIsHintShown] = useState(false);
  const [hintContent, setHintContent] = useState('');

  if (isOver && !isHintShown && hintContent) {
    setIsHintShown(true);
  } else if (!isOver && isHintShown) {
    setIsHintShown(false);
  }

  const secondDropAreaDefault = {
    change: null,
    core: null,
    transaction: null
  };

  const secondDropAreaRef = useRef(secondDropAreaDefault);

  const handleSecondDropAreaItemDrop = (prop, value) => {
    secondDropAreaRef.current[prop] = value;
  }

  const handleItemRemoveFromPrevDropArea = (draggedItem) => {
    const updatedDroppedItems = droppedItems.filter(item => item.id !== draggedItem.id);
  
    if (draggedItem.type === 'change') {
      const valueItemIndex = updatedDroppedItems.findIndex(item => item.type === 'value');
      if (valueItemIndex !== -1) {
        updatedDroppedItems.splice(valueItemIndex, 1);
      }
  
      let oppositeChange = draggedItem.description === 'Increase' ? 'Decrease' : 'Increase';

      let updatedValue = calculateUpdatedValue(oppositeChange, value);
      
      if(isSecondDropArea) {
        onSecondDropAreaItemDrop('change', null);
      } else if(secondDropAreaRef.current.change && updatedDroppedItems.length === 0) {
        oppositeChange = secondDropAreaRef.current.change === 'Increase' ? 'Decrease' : 'Increase';
        const secondDropAreaValue = calculateUpdatedValue(oppositeChange, value);
        updatedValue += secondDropAreaValue;

        if(secondDropAreaRef.current.core) {
          onCoreItemDrop(option.name, {name: secondDropAreaRef.current.core, value: secondDropAreaValue}, 'add');
        }
        
        secondDropAreaRef.current = secondDropAreaDefault;
      }

      onChangeItemDrop(option.name, updatedValue);

      const droppedCoreItem = updatedDroppedItems.find(droppedItem => droppedItem.type === 'core');
      if(droppedCoreItem) {
        oppositeChange = draggedItem.description === 'Increase' ? 'Decrease' : 'Increase';
        const updatedValue = calculateUpdatedValue(oppositeChange, value);
        onCoreItemDrop(option.name, {name: droppedCoreItem.name, value: updatedValue}, 'add');
      }
    } else if (draggedItem.type === 'transaction') {      
      if(!selectedDesignTypes.includes('1') && selectedDesignTypes.includes('2')) {
        const valueItemIndex = updatedDroppedItems.findIndex(item => item.type === 'value');
        if (valueItemIndex !== -1) {
          updatedDroppedItems.splice(valueItemIndex, 1);
        }
    
        let oppositeChange = draggedItem.name === 'Debit' ? 'Credit' : 'Debit';

        let updatedValue = calculateNormalUpdatedValue(option.name, oppositeChange, value);
          if(isSecondDropArea) {
            onSecondDropAreaItemDrop('transaction', null);
          } else if(secondDropAreaRef.current.transaction && updatedDroppedItems.length === 0) {
            oppositeChange = secondDropAreaRef.current.transaction === 'Debit' ? 'Credit' : 'Debit';
            const secondDropAreaValue = calculateNormalUpdatedValue(option.name, oppositeChange, value);
            updatedValue += secondDropAreaValue;

            if(secondDropAreaRef.current.core) {
              onCoreItemDrop(option.name, {name: secondDropAreaRef.current.core, value: secondDropAreaValue}, 'add');
            }

            secondDropAreaRef.current = secondDropAreaDefault;
          }

          onChangeItemDrop(option.name, updatedValue);

          const droppedCoreItem = updatedDroppedItems.find(droppedItem => droppedItem.type === 'core');
          if(droppedCoreItem) {
            oppositeChange = draggedItem.name === 'Debit' ? 'Credit' : 'Debit';
            const updatedValue = calculateNormalUpdatedValue(option.name, oppositeChange, value);
            onCoreItemDrop(option.name, {name: droppedCoreItem.name, value: updatedValue}, 'add');
          }
      } else {
        if(secondDropAreaRef.current.change && updatedDroppedItems.length === 0) {
          let oppositeChange = secondDropAreaRef.current.change === 'Increase' ? 'Decrease' : 'Increase';
          let updatedValue = calculateUpdatedValue(oppositeChange, value);

          onChangeItemDrop(option.name, updatedValue);

          const droppedCoreItem = updatedDroppedItems.find(droppedItem => droppedItem.type === 'core');
          if(secondDropAreaRef.current?.core) {
            onCoreItemDrop(option.name, {name: secondDropAreaRef.current.core, value: updatedValue}, 'add');
          }
          secondDropAreaRef.current = secondDropAreaDefault;
          if(droppedCoreItem) {
            oppositeChange = draggedItem.name === 'Debit' ? 'Credit' : 'Debit';
            const updatedValue = calculateNormalUpdatedValue(option.name, oppositeChange, value);
            onCoreItemDrop(option.name, {name: droppedCoreItem.name, value: updatedValue}, 'add');
          }
        }
      }
      
    } else if(draggedItem.type === 'core') {
      let updatedValue = 0;
      if(selectedDesignTypes.includes('1')) {
        const droppedChangeItem = droppedItems.find(droppedItem => droppedItem.type === 'change');
      
        if(droppedChangeItem) {
          const oppositeChange = droppedChangeItem.description === 'Increase' ? 'Decrease' : 'Increase';
          updatedValue = calculateUpdatedValue(oppositeChange, value);
        }

        if(isSecondDropArea) {
          onSecondDropAreaItemDrop('core', null);
        } else if(secondDropAreaRef.current.change && updatedDroppedItems.length === 0) {
          const oppositeChange = secondDropAreaRef.current.change === 'Increase' ? 'Decrease' : 'Increase';
          const secondDropAreaValue = calculateUpdatedValue(oppositeChange, value);
  
          onChangeItemDrop(option.name, secondDropAreaValue);

          if(secondDropAreaRef.current?.core) {
            onCoreItemDrop(option.name, {name: secondDropAreaRef.current.core, value: secondDropAreaValue}, 'add');
          }
          secondDropAreaRef.current = secondDropAreaDefault;
        }
      } else if(selectedDesignTypes.includes('2')) {
        const droppedTransactionItem = droppedItems.find(droppedItem => droppedItem.type === 'transaction');
        
        if(droppedTransactionItem) {
          const oppositeChange = droppedTransactionItem.name === 'Debit' ? 'Credit' : 'Debit';
          updatedValue = calculateNormalUpdatedValue(option.name, oppositeChange, value);
        }

        if(isSecondDropArea) {
          onSecondDropAreaItemDrop('core', null);
        } else if(secondDropAreaRef.current.transaction && updatedDroppedItems.length === 0) {
          const oppositeChange = secondDropAreaRef.current.transaction === 'Debit' ? 'Credit' : 'Debit';
          const secondDropAreaValue = calculateNormalUpdatedValue(option.name, oppositeChange, value);
  
          onChangeItemDrop(option.name, secondDropAreaValue);

          if(secondDropAreaRef.current?.core) {
            onCoreItemDrop(option.name, {name: secondDropAreaRef.current.core, value: secondDropAreaValue}, 'add');
          }
          secondDropAreaRef.current = secondDropAreaDefault;
        }
      }
      onCoreItemDrop(option.name, {name: draggedItem.name, value: updatedValue}, 'add', true);
    }
  
    onDropAreaChange({
      id: `${option.id}_${+isSecondDropArea}`,
      droppedItemsCount: updatedDroppedItems.length,
      ...(draggedItem.type === 'change' && { changeId: 0 }),
      ...(draggedItem.type === 'core' && { coreId: 0 }),
      ...(draggedItem.type === 'transaction' && { transactionId: 0 }),
    });

    if(updatedDroppedItems.length === 0 && !isSecondDropArea) {
      onDropAreaChange({
        id: `${option.id}_1`,
        droppedItemsCount: 0
      });

      // if(secondDropAreaRef.current.core) {
      //   let updatedValue = 0;
      //   if(selectedDesignTypes.includes('1')) {
      //     const oppositeChange = secondDropAreaRef.current.change === 'Increase' ? 'Decrease' : 'Increase';
      //     updatedValue = calculateUpdatedValue(oppositeChange, value);
      //   } else if(selectedDesignTypes.includes('2')) {
      //     const oppositeChange = secondDropAreaRef.current.transaction === 'Debit' ? 'Credit' : 'Debit';
      //     updatedValue = calculateNormalUpdatedValue(option.name, oppositeChange, value);
      //   }
      //   onCoreItemDrop(option.name, {name: secondDropAreaRef.current.core, value: updatedValue}, 'add');
      // }
    }

    setDroppedItems(updatedDroppedItems);
  };

  const hasDroppedItems = droppedItems.length > 0;
  // Second slot only appears once the first record has all required chips — prevents students from spreading one decision per slot.
  const isFirstAreaFull = optionsToBeFilled > 0
    ? droppedItems.length >= optionsToBeFilled
    : hasDroppedItems;

  return (
    <div className="transaction-record-drop-area-wrapper">
      <div ref={drop} className={`transaction-record-drop-area ${!hasDroppedItems ? 'empty' : ''}`}>
        {
          hasDroppedItems &&
            <DropAreaList 
              items={droppedItems}
              onItemsDropped={handleItemRemoveFromPrevDropArea}
              isItemsDisabled={isItemsDisabled}
            />
        }
        {
          !hasDroppedItems &&
            <>
              <img src={SignPlus} alt="Plus" />
              <span>add a record</span>
            </>
        }
        {
          isHintShown &&
            <Hint 
              content={hintContent}
              side="left"
            />
        }
      </div>
      {
        isFirstAreaFull && !isSecondDropArea &&
          <DropArea
            option={option}
            value={value}
            isSecondDropArea={true}
            onChangeItemDrop={onChangeItemDrop}
            onCoreItemDrop={onCoreItemDrop}
            onDropAreaChange={onDropAreaChange}
            incorrectDecisions={incorrectDecisions}
            onSecondDropAreaItemDrop={handleSecondDropAreaItemDrop}
            isItemsDisabled={isItemsDisabled}
            selectedDesignTypes={selectedDesignTypes}
            tutorialRestrictions={tutorialRestrictions}
            optionsToBeFilled={optionsToBeFilled}
          />
      }
    </div>
  );
}
